import { SmilePlus } from 'lucide-react';
import { groupReactions, QUICK_REACTIONS, type MessageReaction } from '../../../../lib/message-reactions';
import { Button, IconButton } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';

// Reaction chips under a bubble — one per distinct emoji, count when >1. The caller's own chip
// reads as the DS selected state (outlined skin + its active grey fill); clicking any chip toggles
// that emoji. Chips stay clickable on locked (WhatsApp/SMS-owned) threads too: reactions are in-app
// annotations, not channel sends.
export function ReactionChips({
  reactions,
  onToggle,
}: {
  reactions: MessageReaction[] | undefined;
  onToggle?: (emoji: string) => void;
}) {
  const groups = groupReactions(reactions);
  if (groups.length === 0) return null;
  return (
    <div style={CHIPS_ROW}>
      {groups.map((g) => {
        const who = g.who.join(', ');
        // A clickable toggle pill IS a DS Button, and every chip is the outlined skin. The caller's
        // OWN reaction reads as SELECTED via the DS selected fill (`--ds-fill-secondary-active`, the
        // outlined skin's active grey) + primary ink — the same way the nav rail marks the current
        // route with the ghost-active fill, rather than the high-emphasis solid/primary button.
        return (
          <Button
            key={g.emoji}
            variant="outlined"
            color="standard"
            size="tiny"
            onClick={onToggle ? () => onToggle(g.emoji) : undefined}
            aria-pressed={g.mine}
            aria-label={`${g.emoji} ${who}`}
            title={who}
            style={g.mine ? { background: 'var(--ds-fill-secondary-active)' } : undefined}
          >
            <span aria-hidden>{g.emoji}</span>
            {g.count > 1 && <span style={CHIP_COUNT}>{g.count}</span>}
          </Button>
        );
      })}
    </div>
  );
}

// The smiley toggle in the tool bar (Figma 14511:160345 "When Click on Emoji icon", glyph per
// 14623:185150 — a smiley WITH a plus badge, i.e. lucide's SmilePlus, not the bare Smile): the DS
// Symphony Icon Button; clicking it opens the quick-react bar below and holds the ghost/hover
// fill while open. Always visible, like the rest of the tool bar.
export function ReactToggleButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton
      size="small"
      variant="text"
      onClick={onToggle}
      aria-label={t('chat.reactions.react')}
      aria-expanded={open}
      title={t('chat.reactions.react')}
      style={open ? { background: 'var(--ds-surface-light-gray)' } : undefined}
    >
      <SmilePlus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

// Quick-react bar — the emoji row the smiley reveals (Figma emoji bar: 30px ghost icon buttons,
// gap 2). Picking one toggles that reaction; the parent closes the bar.
export function QuickReactBar({ onToggle }: { onToggle: (emoji: string) => void }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('chat.reactions.react')} style={QUICK_BAR}>
      {QUICK_REACTIONS.map((emoji) => (
        <IconButton key={emoji} size="small" variant="text" onClick={() => onToggle(emoji)} aria-label={emoji} title={emoji}>
          <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>
            {emoji}
          </span>
        </IconButton>
      ))}
    </div>
  );
}

const CHIPS_ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 4,
};

const CHIP_COUNT: React.CSSProperties = {
  marginInlineStart: 4,
  fontSize: 11.5,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'currentColor',
  opacity: 0.7,
};

const QUICK_BAR: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
};
